import React, { ReactNode, useMemo, useState } from "react";
import { Button, Col, Form, Input, Row, Select, Spin, Tooltip } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading, getDictionaryItemById, getSelectOptions } from "../../../../lib/utils";
import { IChangingRecordKeyNumber } from "../../../../lib/types/IViewMode";
import { hasAccess } from "../../../../components/RolesModule/hasAccess";
import { MENU_ADMIN_CHAR_TYPE_WRITE } from "../../../../lib/const/permissions";
import "./TypesCharacteristicsParams.less";
import { TypeCharacteristic } from "../../types/TypeCharacteristic";
import { fetchTypesCharacteristicsSelectValueDic } from "../../store/slice/typesCharacteristicsSlice";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import HistoryAndPropertyModal from "../../../../components/HistoryAndPropertyModal/HistoryAndPropertyModal";

interface ComponentProps {
  mode: "create" | "view" | "edit_close_date" | "edit";
  typeCharacteristic?: TypeCharacteristic;
  typesCharacteristicsParamsForm?: FormInstance<any>;
  isLoading?: boolean;
  isChild?: boolean;
  onChangeDataTypeId?: (value: string) => void;
  selectedDataTypeId?: string;
}

export const TypesCharacteristicsParams = ({
  mode,
  typeCharacteristic,
  typesCharacteristicsParamsForm,
  isLoading = false,
  isChild = false,
  onChangeDataTypeId,
  selectedDataTypeId,
}: ComponentProps) => {
  const {
    typesCharacteristicsTypesDataDic,
    typesCharacteristicsTypesDataDicLoading,
    typesCharacteristicsTypesDic,
    typesCharacteristicsTypesDicLoading,
  } = useAppSelector((state) => state.typesCharacteristics);

  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [objectId, setObjectId] = useState<number | null>(null);

  const showHistoryModal = (objectId: number) => {
    setHistoryModalVisible(true);
    setObjectId(objectId);
  };

  

  const dispatch = useAppDispatch();

  const renderDataItem = (props: {
    label: string;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperCol?: number;
    tooltipTitle?: string;
  }): ReactNode => {
    const {
      label,
      data,
      offsetCol = 0,
      spanCol = 6,
      labelCol = 8,
      wrapperCol = 16,
      tooltipTitle,
    } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row className="types-characteristics-params__dataRow">
          <Col span={labelCol}>
            {tooltipTitle ? (
              <Tooltip title={tooltipTitle}>
                <label>{label}</label>
              </Tooltip>
            ) : (
              <label>{label}</label>
            )}
          </Col>
          <Col span={wrapperCol}>
            <span>{data}</span>
          </Col>
        </Row>
      </Col>
    );
  };

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    return "row_child";
  }, [typeCharacteristic, isChild]);

  return (
    <Row
      className={`types-characteristics-params ${childClassName}`}
      style={{ width: "100%", marginBottom: mode !== "create" ? 28 : undefined }}
    >
      <Spin spinning={isLoading}>
        <Row wrap={false}>
          <Col style={{ width: "100%" }}>
            <Row
              gutter={50}
              style={{ width: "100%", marginBottom: mode !== "create" ? 18 : undefined }}
            >
              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item
                    label="Наименование"
                    name="name"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Input
                      maxLength={255}
                      style={{ width: 220 }}
                      placeholder="Введите текст..."
                      allowClear
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>{renderDataItem({ label: "Наименование", data: typeCharacteristic?.name })}</>
              )}

              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item
                    // Заглушка
                    name="dataTypeId"
                    style={{ display: "none" }}
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  />
                  <Form.Item
                    label="Тип данных"
                    // Управляем значением через onChange
                    // name="dataTypeId"
                    name=""
                    labelCol={{ span: 10 }}
                    wrapperCol={{ span: 14 }}
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Select
                      value={selectedDataTypeId}
                      options={getSelectOptions({
                        list: typesCharacteristicsTypesDataDic,
                      })}
                      loading={checkIsLoading(typesCharacteristicsTypesDataDicLoading)}
                      style={{ width: 200 }}
                      placeholder="Выбрать"
                      allowClear
                      onChange={onChangeDataTypeId}
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>
                  {renderDataItem({
                    label: "Тип данных",
                    data: getDictionaryItemById(
                      typesCharacteristicsTypesDataDic,
                      typeCharacteristic?.dataTypeId
                    )?.name,
                    labelCol: 10,
                    wrapperCol: 14,
                  })}
                </>
              )}

              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item dependencies={["dataTypeId"]} noStyle>
                    {({ getFieldValue }) => {
                      const dataTypeId = getFieldValue("dataTypeId");
                      return (
                        <Form.Item
                          label="Словарь (значение)"
                          name="valueDicTypeAlias"
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                          rules={[
                            {
                              required: dataTypeId === 4,
                              message: "",
                            },
                          ]}
                        >
                          <Select
                            options={getSelectOptions({
                              list: [...typesCharacteristicsTypesDic].sort((a, b) =>
                                a.name.localeCompare(b.name)
                              ),
                              valueName: "alias",
                            })}
                            loading={checkIsLoading(typesCharacteristicsTypesDicLoading)}
                            style={{ width: 200 }}
                            placeholder="Выбрать"
                            allowClear
                            onChange={(value: string) => {
                              if (value) {
                                dispatch(fetchTypesCharacteristicsSelectValueDic(value));
                              }
                            }}
                            showSearch
                            filterOption={(input, option) =>
                              String(option?.label)?.toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={dataTypeId !== 4}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
              ) : (
                <>
                  {renderDataItem({
                    label: "Словарь (значение)",
                    data: getDictionaryItemById(
                      typesCharacteristicsTypesDic,
                      typeCharacteristic?.valueDicTypeId
                    )?.name,
                    labelCol: 11,
                    wrapperCol: 13,
                  })}
                </>
              )}

              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item
                    label="Комментарий"
                    name="dsc"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                  >
                    <Input
                      style={{ width: 220 }}
                      maxLength={255}
                      placeholder="Введите текст..."
                      allowClear
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>
                  {renderDataItem({
                    label: "Комментарий",
                    data: (
                      <Row>
                        <Col span={11}>{typeCharacteristic?.dsc}</Col>
                      </Row>
                    ),
                  })}
                </>
              )}
            </Row>

            <Row gutter={50} style={{ width: "100%" }}>
              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item
                    label="Alias"
                    name="alias"
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                  >
                    <Input
                      maxLength={255}
                      style={{ width: 220 }}
                      placeholder="Введите текст..."
                      allowClear
                    />
                  </Form.Item>
                </Col>
              ) : (
                <>{renderDataItem({ label: "Alias", data: typeCharacteristic?.alias })}</>
              )}

              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item
                    label={<Tooltip title="мин | макс">Количество значений</Tooltip>}
                    labelCol={{ span: 10 }}
                    wrapperCol={{ span: 14 }}
                    dependencies={["minCardinality", "maxCardinality"]}
                  >
                    {({ getFieldsValue, validateFields }) => {
                      const { minCardinality, maxCardinality } = getFieldsValue();

                      return (
                        <Row wrap={false}>
                          <Form.Item
                            label=""
                            name="minCardinality"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value && isNaN(value)) {
                                    return Promise.reject();
                                  }
                                  return Promise.resolve();
                                },
                              },
                              {
                                validator: (_, value) => {
                                  let isSucces = true;

                                  if (value && maxCardinality && isSucces) {
                                    isSucces =
                                      Number(value) <= Number(maxCardinality) && Number(value) >= 0;
                                  }
                                  return isSucces ? Promise.resolve() : Promise.reject();
                                },
                              },
                            ]}
                          >
                            <Input
                              style={{ width: 56, height: 32 }}
                              onChange={() => {
                                validateFields(["maxCardinality"]);
                              }}
                            />
                          </Form.Item>

                          <span className="cardinality-value">|</span>

                          <Form.Item
                            label=""
                            name="maxCardinality"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (value && isNaN(value)) {
                                    return Promise.reject();
                                  }
                                  return Promise.resolve();
                                },
                              },
                              {
                                validator: (_, value) => {
                                  let isSucces = true;

                                  if (value && minCardinality && isSucces) {
                                    isSucces =
                                      Number(value) >= Number(minCardinality) && Number(value) >= 0;
                                  }
                                  return isSucces ? Promise.resolve() : Promise.reject();
                                },
                              },
                            ]}
                          >
                            <Input
                              style={{ width: 56, height: 32 }}
                              onChange={() => {
                                validateFields(["minCardinality"]);
                              }}
                            />
                          </Form.Item>
                        </Row>
                      );
                    }}
                  </Form.Item>
                </Col>
              ) : (
                <>
                  {renderDataItem({
                    label: "Количество значений",
                    tooltipTitle: "мин | макс",
                    data: (
                      <>
                        {typeCharacteristic?.minCardinality}
                        <span className="cardinality-value">|</span>
                        {typeCharacteristic?.maxCardinality}
                      </>
                    ),
                    labelCol: 10,
                    wrapperCol: 14,
                  })}
                </>
              )}

              {mode === "create" ? (
                <Col span={6}>
                  <Form.Item dependencies={["dataTypeId"]} noStyle>
                    {({ getFieldsValue }) => {
                      const { dataTypeId } = getFieldsValue();
                      return (
                        <Form.Item
                          label="Словарь (параметр)"
                          name="specificDicTypeAlias"
                          labelCol={{ span: 11 }}
                          wrapperCol={{ span: 13 }}
                        >
                          <Select
                            options={getSelectOptions({
                              list: [...typesCharacteristicsTypesDic].sort((a, b) =>
                                a.name.localeCompare(b.name)
                              ),
                              valueName: "alias",
                            })}
                            loading={checkIsLoading(typesCharacteristicsTypesDicLoading)}
                            style={{ width: 200 }}
                            placeholder="Выбрать"
                            allowClear
                            showSearch
                            filterOption={(input, option) => {
                              return String(option?.label)
                                ?.toLowerCase()
                                .includes(input.toLowerCase());
                            }}
                            disabled={dataTypeId !== 4}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Col>
              ) : (
                <>
                  {renderDataItem({
                    label: "Словарь (параметр)",
                    data: getDictionaryItemById(
                      typesCharacteristicsTypesDic,
                      typeCharacteristic?.specificDicTypeId
                    )?.name,
                    labelCol: 11,
                    wrapperCol: 13,
                  })}
                </>
              )}
            </Row>
          </Col>
          <Col style={{ display: "flex", alignItems: "center" }}>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                showHistoryModal(Number(typeCharacteristic?.id));
              }}
              className="iconButton types-characteristics-values-table__threeDotsButton"
              type="text"
              htmlType="button"
              icon={<DotsIcon />}
            />
            <HistoryAndPropertyModal
              objectTypeSysname="char_type"
              objectId={objectId}
              handleHideModal={() => {
                setHistoryModalVisible(false);
              }}
              visibleModal={historyModalVisible}
              hasAccessToEdit={hasAccess(MENU_ADMIN_CHAR_TYPE_WRITE)}
            />
          </Col>
        </Row>
      </Spin>
    </Row>
  );
};

export default TypesCharacteristicsParams;
